import * as CONSTANTS from './constants'
import { InvalidUsage } from '../exceptions/InvalidUsage';
import { ActionLogger } from './ActionLogger';

/**
 * Manages RedHerrings. RedHerrings make a Node look like it might have trouble until it is investigated even if there are no problems
 */
export class RedHerringManager {
    numRedHerringsStart;
    numRedHerringsAdd;
    roleRedHerrings = {
        [CONSTANTS.SOC]: [],
        [CONSTANTS.IT]: [],
        [CONSTANTS.PIO]: [],
        [CONSTANTS.SCADA]: []
    }

    /**
     * factory function for building RedHerringManager objects
     * @param {number} numRedHerringsStart
     * @param {number} numRedHerringsAdd
     * @return {RedHerringManager} instance
     */
    static build(numRedHerringsStart, numRedHerringsAdd) {
        let instance = new RedHerringManager();
        instance.numRedHerringsStart = numRedHerringsStart;
        instance.numRedHerringsAdd = numRedHerringsAdd;
        return instance;
    }

    /**
     * add RedHerrings to Healthy Nodes
     * @param {Storage} storage
     * @param {SessionConfig} sessionConfig
     * @param {string} role
     * @param {number} round
     * @param {bool} gameInProgress
     */
    injectRedHerrings(storage, sessionConfig, role, round, gameInProgress) {
        let graph = this.graphByRole(storage, role, gameInProgress);
        let newRedHerrings = [];
        let infectedNodes = storage.getNodesByStatus(CONSTANTS.COMPROMISED);

        let shutoffNodes = storage.getNodesByStatus(CONSTANTS.OFF);
        let redHerrings = this.roleRedHerrings[role];
        let numToInject = 0;
        if (round === 1) {
            numToInject = this.numRedHerringsStart;
        }
        else if (round > 1 && round <= sessionConfig.getTotalRounds()) {
            numToInject = this.numRedHerringsAdd;
        }
        else {
            throw new InvalidUsage(`invalid round number: ${round}/TOTAL_ROUNDS: ${CONSTANTS.TOTAL_ROUNDS}`);
        }

        // # will be updated as nodes are added
        let offLimitsNodes = [...infectedNodes, ...redHerrings, ...shutoffNodes];

        // #populates the graph with red herrings - adds one less than the total so a coordinated red herring can be added later
        for (let i = 0; i < numToInject; i++) {
            let newRedHerring = storage.getRandomNodeName(Object.keys(graph["nodes"]));
            storage.appendRoleToAttackVisibility(newRedHerring, role);
            newRedHerrings.push(newRedHerring);
            offLimitsNodes.push(newRedHerring);
        }

        this.roleRedHerrings[role] = [...redHerrings, ...newRedHerrings]; // for storing information between rounds

        // #log values of new red herrings for the role - can be adjusted to accomodate an initial message based on the round
        let logger = ActionLogger.getLogger();
        logger.log('new uncoordinated red herring nodes for ' + role + ': ' + JSON.stringify(newRedHerrings));
    }

    /**
     * @param {Storage} storage
     * @param {string} role
     * @param {bool} gameInProgress
     */
    coordinateRedHerrings(storage, role, gameInProgress) {
        let visibleNodesGraph = this.graphByRole(storage, role, gameInProgress);
        let otherRoles = CONSTANTS.ALL_ROLES.filter(r => r !== role); // This might be different from the PHP (possible mistake in PHP)
        let roleToPull = RedHerringManager.pickRandomValue(otherRoles);
        let pulledRedHerring = RedHerringManager.pickRandomValue(this.roleRedHerrings[roleToPull]);

        // # check that selected red herring is not already in that role's list
        // #may be possible to hit infinite loop if none of the other_roles have devices that are available or visible to role
        if (this.roleRedHerrings[role].includes(pulledRedHerring) || (!Object.keys(visibleNodesGraph["nodes"]).includes(pulledRedHerring))) {
            //TODO this is broken and creating an infinte loop when the following line is uncommented
            //this.coordinateRedHerrings(storage, role, gameInProgress);
            return;
        }
        let logger = ActionLogger.getLogger();
        if (pulledRedHerring !== null) {
            this.roleRedHerrings[role].push(pulledRedHerring);
            storage.appendRoleToAttackVisibility(pulledRedHerring, role);
            logger.log('new coordinated red herring node for ' + role + ': ' + pulledRedHerring);
        } else {
            logger.log('pulled redHerring was null. Perhaps there is an issue here');
        }
    }

    /**
     * get the view from storage based upon the users $role 
     * @param {Storage} storage
     * @param {string} role
     * @param {bool} gameInProgress
     * @return {Object|InvalidUsage}
     */
    graphByRole(storage, role, gameInProgress) {
        return role === CONSTANTS.PIO ? storage.getViewPio(gameInProgress) :
            (role === CONSTANTS.IT ? storage.getViewIt(gameInProgress) :
            (role === CONSTANTS.SOC ? storage.getViewSoc(gameInProgress) :
            (role === CONSTANTS.SCADA ? storage.getViewScada(gameInProgress) :
            new InvalidUsage("invalid role:"))));
    }

    toJSON() {
        return {
            num_red_herrings_start: this.numRedHerringsStart,
            num_red_herrings_add: this.numRedHerringsAdd,
            role_red_herrings: this.roleRedHerrings
        };
    }

    static jsonDeserialize(json) {
        let instance = new RedHerringManager();
        let data = json;
        instance.numRedHerringsStart = data.num_red_herrings_start;
        instance.numRedHerringsAdd = data.num_red_herrings_add;
        instance.roleRedHerrings = data.role_red_herrings;
        return instance;
    }

    /**
     * pick a random value from an array
     * @param array
     * @return valueFromArray | null if array is empty
     */
    static pickRandomValue(array) {
        if (array.length === 0) return null;
        return Object.values(array)[Math.floor(Math.random() * Object.values(array).length)];
    }

    getRoleRedHerrings(role) {
        return this.roleRedHerrings[role];
    }
}